import React from 'react';
import { useAtom } from 'jotai';
import { Task } from '../../../../types/task';
import { getTaskTimeInfo } from '../../../../utils/taskSortUtils';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';

interface CalendarDayItemProps {
  date: Date;
  currentDate?: Date;
  allTasks?: Task[];
  onClick?: (date: Date) => void;
  showMonth?: boolean; // 是否显示月份
  isCurrentMonth?: boolean; // 是否当前月份（影响透明度）
  disableHighlight?: boolean; // 是否禁用高亮（特殊模式下使用）
  className?: string;
}

/**
 * 可复用的日历日期项组件
 * 支持任务密度显示和不同的显示变体
 */
export const CalendarDayItem: React.FC<CalendarDayItemProps> = ({
  date,
  currentDate,
  allTasks = [],
  onClick,
  showMonth = false,
  isCurrentMonth = true,
  disableHighlight = false,
  className = ''
}) => {
  const [isDark] = useAtom(isDarkAtom);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const dateOnly = new Date(date);
  dateOnly.setHours(0, 0, 0, 0);
  
  const currentDateOnly = currentDate ? new Date(currentDate) : null;
  if (currentDateOnly) {
    currentDateOnly.setHours(0, 0, 0, 0);
  }

  // 计算日期状态
  const isSelected = !disableHighlight && currentDateOnly && dateOnly.getTime() === currentDateOnly.getTime();
  const isToday = dateOnly.getTime() === today.getTime();
  
  // 获取当天的任务信息
  const getTaskInfoForDate = (targetDate: Date) => {
    const targetDateOnly = new Date(targetDate);
    targetDateOnly.setHours(0, 0, 0, 0);

    const dayTasks = allTasks.filter(task => {
      const timeInfo = getTaskTimeInfo(task);
      if (!timeInfo.hasTime || !timeInfo.primaryTime) return false;
      
      const taskDate = new Date(timeInfo.primaryTime);
      taskDate.setHours(0, 0, 0, 0);
      
      return taskDate.getTime() === targetDateOnly.getTime();
    });

    // 按优先级分组统计
    const priorityCount = {
      vital: dayTasks.filter(t => t.vital && t.urgent).length,
      important: dayTasks.filter(t => t.vital && !t.urgent).length,
      urgent: dayTasks.filter(t => !t.vital && t.urgent).length,
      normal: dayTasks.filter(t => !t.vital && !t.urgent).length,
    };

    return {
      total: dayTasks.length,
      priorityCount,
      tasks: dayTasks
    };
  };

  const taskInfo = getTaskInfoForDate(date);

  // 渲染任务密度圆点（按优先级着色）
  const renderTaskDots = () => {
    const { priorityCount } = taskInfo;
    const dots = [];

    if (taskInfo.total > 0) {
      // 根据是否选中决定颜色方案
      const priorities = isSelected ? [
        // 选中日期：使用不同深浅的白色
        { count: priorityCount.vital, color: '#FFFFFF' },     // 纯白
        { count: priorityCount.important, color: '#F3F4F6' }, // 浅灰白
        { count: priorityCount.urgent, color: '#E5E7EB' },    // 中灰白
        { count: priorityCount.normal, color: '#D1D5DB' }     // 深灰白
      ] : [
        // 非选中日期：使用原来的彩色
        { count: priorityCount.vital, color: '#EF4444' },     // 红色
        { count: priorityCount.important, color: '#F97316' }, // 橙色
        { count: priorityCount.urgent, color: '#EAB308' },    // 黄色
        { count: priorityCount.normal, color: '#9CA3AF' }     // 灰色
      ];

      let dotIndex = 0;
      for (const priority of priorities) {
        for (let i = 0; i < priority.count && dotIndex < 5; i++) {
          dots.push(
            <div
              key={dotIndex}
              className="w-1 h-1 rounded-full"
              style={{ backgroundColor: priority.color }}
            />
          );
          dotIndex++;
        }
      }
    }

    // 始终返回一个固定高度的容器，确保对齐
    return (
      <div className="flex justify-center space-x-0.5 mt-1 h-2 items-center">
        {dots}
      </div>
    );
  };

  // 处理点击事件
  const handleClick = () => {
    if (onClick) {
      onClick(date);
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`
        relative p-2 text-sm rounded-lg transition-all duration-200 flex flex-col items-center justify-center
        ${isSelected
          ? 'bg-blue-500 text-white shadow-sm scale-105'
          : isToday
            ? isDark
              ? 'bg-blue-900/30 text-blue-400 border border-blue-700/50'
              : 'bg-blue-50 text-blue-600 border border-blue-200'
            : isCurrentMonth
              ? isDark
                ? 'text-ios-dark-gray1 hover:bg-ios-dark-gray5 hover:scale-105'
                : 'text-gray-900 hover:bg-gray-50 hover:scale-105'
              : isDark
                ? 'text-ios-dark-gray3 hover:bg-ios-dark-gray5/50'
                : 'text-gray-400 hover:bg-gray-25'
        }
        ${className}
      `}
      title={date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      })}
    >
      {/* 月份显示（可选） - 固定占位 */}
      <div className={`text-xs mb-1 leading-none h-3 flex items-center justify-center ${
        isDark ? 'text-ios-dark-gray3' : 'text-gray-400'
      }`}>
        {showMonth && (
          date.toLocaleDateString('zh-CN', {
            month: 'short'
          })
        )}
      </div>

      {/* 日期数字 */}
      <div className="text-center font-medium flex-1 flex items-center justify-center">
        {date.getDate()}
      </div>

      {/* 任务密度圆点 - 固定占位 */}
      {renderTaskDots()}
    </button>
  );
};
